<template lang="pug">
    label(
        class="_radio",
        :class="_class",
        :style="{ width, height }",
        v-on="__on")
        input(
            ref="_in",
            type="radio",
            :id="id",
            :name="name",
            :value="val",
            :required="required",
            :disabled="disabled",
            :inert="inert",
            :tabindex="inert ? -1 : tabindex",
            v-on="__on",
            @change="changed")
        span(
            class="_cbc",
            :class="{ ['_'+tone]: tone, _fvis: this.fvis }",
            aria-hidden="true") &zwnj;
        span(
            v-if="$slots.default",
            class="_cbl")
            slot
</template>

<script>
    import entry from './ib-form-entry.vue';

    /**
     * A stylized radio button control that supports all v-model behavior of the raw input control.
     */
    export default {
        name: 'ib-radio',

        mixins: [
            entry
        ],

        props: {
            /**
            * Toggles intended value data type
            */
            value: {
                type: [
                    String,   // checked = set to this.val
                    Number,   // checked = set to this.val
                    Boolean,  // checked = set to true
                    Array     // checked = contains this.val
                ],
                default: undefined
            },
            val: [String,Number],
            /**
             * Appearance modifier: `frameless` is supported on the ios platform.
             */
            appearance: String,
            /**
             * Take focus when made visible.
             */
            autofocus: Boolean,
            /**
             * Semantic color class.
             */
            tone: String
        },

        data() {
            return {
                fvis: false
            };
        },

        computed: {
            _class() {
                return {
                    _fo: this.focused,
                    _inv: this.showvalid && !this.valid,
                    ['_e'+this.appearance]: this.appearance
                }
            }
        },

        watch: {
            val() {
                this._sync();
            },
        },

        methods: {
            /** Callback for check/uncheck */
            changed() {
                this.validate();
                this.input(this.$refs._in.checked);
            },

            /** Can be called externally to simulate a click */
            click() {
                this.input(true);
                this.$refs._in.focus();
            },

            /** Retrieve desired check state based on value */
            checked(value) {
                if (value instanceof Array) {
                    return value.indexOf(this.val) >= 0;
                } else if (typeof value == 'Boolean') {
                    return value;
                } else {
                    return (this.val == value);
                }
            },

            /** @private */
            _focus(e) {
                this.fvis = e.timeStamp < __app.tabts;
            },

            /** @private */
            _blur(e) {
                this.fvis = false;
            },

            /** @private */
            _click(e) {
                this.click();
                e.preventDefault();
            },

            /** @private */
            _sync() {
                const input = this.$refs._in;
                const checked = this.checked(this.ivalue);
                if (input.checked != checked) {
                    input.checked = checked;
                    this.validate();
                }
            },

            input(checked) {
                if (typeof this.ivalue == 'Boolean') {
                    this._set(checked);
                } else if (checked) {
                    this._set(this.ivalue instanceof Array ? [this.val] : this.val);
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._radio {
        > ._cbc {
            width: @radio-size;
            height: @radio-size;
            line-height: @radio-lh;
            color: @fg-accent;
            .radio-default();
        }
        &._eframeless > ._cbc {
            .radio-frameless();
        }
        > input:hover + ._cbc {
            .radio-hover();
        }
        &._fo > ._cbc {
            .radio-focus();
        }
        > input:checked + ._cbc {
            .radio-checked();
        }
        &._eframeless > input:checked + ._cbc {
            .radio-frameless-checked();
        }
    }
</style>

<ib-example id="tone" name="Tone">
    <template lang="pug">
        ib-form
            .after-8 Food group: 
                span(id="value") {{value || 'None'}}
            .before-4
                ib-radio(name="group", v-model="value", val="milk") The Milk Group
            .before-4
                ib-radio(name="group", v-model="value", val="meat", tone="error") The Meat Group
            .before-4
                ib-radio(name="group", v-model="value", val="fruit", tone="positive") The Fruit Group
            .before-4
                ib-radio(name="group", v-model="value", val="bread", tone="buy") The Bread Group
            .before-4
                ib-radio(name="group", v-model="value", val="beer", tone="important") The Beer Group
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="attrs" name="Attributes">
    <template lang="pug">
        ib-form
            .after-8 Value: 
                span(id="value") {{value || 'None'}}
            .before-4
                ib-radio(name="attrs", v-model="value", val="disabled", disabled) disabled
            .before-4
                ib-radio(name="attrs", v-model="value", val="readonly", readonly) readonly
            .before-4
                ib-radio(name="attrs", v-model="value", val="inert", inert) inert
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>
